import { LegacyCardContent, LegacyCardHeader } from "../card/LegacyCard";
import { OrderPaymentType } from "@sellar-io/api-v2/types";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { BuyerOnly } from "../authFilter/BuyerOnly";
import { SupplierOnly } from "../authFilter/SupplierOnly";
import { Button, H5, Icon } from "@sellar-io/components";
import { item, OrderSummaryItemList } from "./OrderSummaryItemList";
import { OrderSummaryPrice, OrderSummaryPriceProps } from "./OrderSummaryPrice";
import { Dialog, DialogNavigation, DialogCTA } from "../modal/Dialog";
import { useState } from "react";

export type OrderSummaryContainerProps = {
  items: item[];
  cancelledAt?: Date | null;
  paymentDue?: Date | null;
  confirmedAt?: Date | null;
  supplierName?: string;
  showEditCopy?: boolean;
  stripeInvoiceId?: string | null;
  priceProps?: OrderSummaryPriceProps;
  onEditClick?: () => void;
  paymentType?: OrderPaymentType | null;
  paymentTerms?: "credit" | "proforma" | "paymentOnDelivery" | null;
  onViewOrderHistory?: () => void;
  checkoutVersion?: number;
};

export const OrderSummaryContainer = (props: OrderSummaryContainerProps) => {
  const { business } = useSellarCurrentBusiness();
  const showEditButton = () => {
    if (business?.Supplier && props.paymentType === "sellarCredit") {
      return false;
    }

    if (props.paymentType === "checkout" && business?.Retailer) return false;
    if (props.paymentType === "cardPaymentWithDeposit") return false;
    if (props.paymentType === "cardPayment") return false;

    const isCheckoutOrNoPaymentDue =
      !props.paymentDue ||
      props.paymentTerms === "paymentOnDelivery" ||
      props.paymentType === "checkout";
    return !!(
      !props.cancelledAt &&
      props.onEditClick &&
      !props.confirmedAt &&
      !props.stripeInvoiceId &&
      isCheckoutOrNoPaymentDue
    );
  };

  const showEditSellarCreditButtonForSuppliers = () => {
    if (
      business?.Supplier &&
      props.paymentType === "sellarCredit" &&
      !props.cancelledAt &&
      !props.confirmedAt
    ) {
      return true;
    }

    return false;
  };

  const canEditOrder =
    props.paymentType !== "checkout" &&
    props.paymentType !== "cardPaymentWithDeposit" &&
    props.paymentType !== "cardPayment";

  return (
    <>
      <div>
        <LegacyCardHeader>
          <div className={"flex flex-wrap items-center justify-between"}>
            <div className="flex flex-col gap-2">
              <H5>Order Summary</H5>
              {props.showEditCopy && !props.cancelledAt && (
                <>
                  {!props.confirmedAt &&
                  !props.stripeInvoiceId &&
                  (!props.paymentDue ||
                    props.paymentTerms === "paymentOnDelivery") ? (
                    <>
                      <SupplierOnly>
                        {props.paymentType === "sellarCredit" ? (
                          <p>This order is awaiting finalisation.</p>
                        ) : (
                          <p>You can edit this order before finalising.</p>
                        )}
                      </SupplierOnly>
                      {canEditOrder && (
                        <BuyerOnly>
                          <p>
                            You can still edit this order before{" "}
                            {props.supplierName} finalises it.
                          </p>
                        </BuyerOnly>
                      )}
                    </>
                  ) : (
                    <>
                      {!props.confirmedAt && props.paymentDue ? (
                        <p>
                          The invoice for this order has been issued and it can
                          no longer be edited.
                        </p>
                      ) : (
                        <p>
                          This order has been finalised and can no longer be
                          edited.
                        </p>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
            <div className="flex gap-2">
              {props.onViewOrderHistory && props.checkoutVersion === 2 && (
                <Button color={"secondary"} onClick={props.onViewOrderHistory}>
                  <Icon size={0.7} icon={"HISTORY"} />
                  View history
                </Button>
              )}
              {showEditButton() && (
                <Button className={"print:hidden"} onClick={props.onEditClick}>
                  <Icon size={0.7} icon={"PENCIL"} />
                  Edit
                </Button>
              )}
              {showEditSellarCreditButtonForSuppliers() && (
                <EditSellarCreditButtonForSuppliers />
              )}
            </div>
          </div>
        </LegacyCardHeader>
        <LegacyCardContent>
          <div className="flex flex-col gap-2">
            <OrderSummaryItemList
              items={props.items}
              printPrice={!!props.priceProps}
            />
            {props.priceProps && (
              <OrderSummaryPrice {...props.priceProps} print={true} />
            )}
          </div>
        </LegacyCardContent>
      </div>
    </>
  );
};

const EditSellarCreditButtonForSuppliers = () => {
  const [active, setActive] = useState(false);
  return (
    <>
      <Button className={"print:hidden"} onClick={() => setActive(true)}>
        <Icon size={0.7} icon={"PENCIL"} />
        Edit
      </Button>
      <Dialog active={active} onDismiss={() => setActive(false)}>
        <DialogNavigation
          leftIcon="CHEVRON_DOWN"
          onClickLeftIcon={() => setActive(false)}
          title="Ask your customer to make order edits"
          description="Orders paid for by Sellar Credit can only be edited by your customer. Each order edit voids the previous request for credit with our third party credit provider and makes a new one for the updated order total. Only the Sellar Credit account holder is permitted to do this."
        />
        <DialogCTA className={"w-full"}>
          <Button className={"w-full"} onClick={() => setActive(false)}>
            Close
          </Button>
        </DialogCTA>
      </Dialog>
    </>
  );
};
